import ConfigUtil from "core/util/config/ConfigUtil";
import { iDef } from "core/util/TypeUtils";
import IService from "../IService";

/**
 * service type that handles uploading files.
 */

/**
 * options for validating the file, callbacks, etc
 */
export interface IUploadOptions {
	allowTypes?: string[];
	maxSize?: number; // bytes
	onProgress?: (e: ProgressEvent) => void;
}

/**
 * upload success value
 */
export interface IUploadResponse {
	url: string;
	name: string;
	type: string;
}

/**
 * reasons that the upload failed
 */
export const UploadErrors = {
	UNKNOWN: "An unknown error occured while uploading the file.",
	FILE_TYPE: "Invalid file type (${type})",
	SIZE: "File is too large (${size})",
	AUTHORIZE: "Unable to authorize upload",
	UPLOAD: "Failed to upload file"
};

export interface IUploadError {
	type: keyof typeof UploadErrors;
	template: string;
	args: any;
}

export abstract class IUploadService implements IService {
	protected static DEFAULT_ID = "default";
	private static CONFIG_FIELD: string = "service.upload";
	private static REGISTRY: string = "upload";

	public static getError(type: keyof typeof UploadErrors, args?: object): IUploadError {
		if (type === 'UNKNOWN' || !UploadErrors[type]) {
			return {
				type: 'UNKNOWN',
				template: UploadErrors['UNKNOWN'],
				args
			};
		}

		return {
			type,
			template: UploadErrors[type],
			args
		};
	}

	public getRegistry(): string {
		return IUploadService.REGISTRY;
	}

	public getConditions() { return undefined; }

	public getActive(): string {
		return ConfigUtil.getString(IUploadService.CONFIG_FIELD, IUploadService.DEFAULT_ID);
	}

	/**
	 * use this method to initialize the upload
	 * @param file the file to upload
	 * @param options configuration for the upload
	 * @returns promise object that will resolve with successful upload details, reject with list of reasons
	 * resolve -> IUploadResponse
	 * reject -> IUploadError[]
	 */
	public upload(file: File, options: IUploadOptions = {}): Promise<IUploadResponse> {
		return new Promise((resolve, reject) => {
			if (this.validateOptions(file, options, reject as (errors: IUploadError[]) => void)) {
				this.handleUpload(file, options, resolve, reject as (errors: IUploadError[]) => void);
			} else {
				reject([this.getError('UNKNOWN')]);
			}
		});
	}

	/**
	 * get the uuid/name of the impl of this service type
	 */
	public abstract getServiceId(): string;

	/**
	 * do the upload in this method
	 * @param file the file to upload
	 * @param options configuration for the upload
	 * @param resolve resolve the upload promise
	 * @param reject reject the upload promise
	 */
	protected abstract handleUpload(
		file: File,
		options: IUploadOptions,
		resolve: (res: IUploadResponse) => void,
		reject: (reasons: IUploadError[]) => void
	): void;

	/**
	 * validate the file against the upload options.
	 * this is used with all implementations of IUploadService.
	 * any additional validation for a specific type of upload can be handled in handleUpload()
	 * @param file
	 * @param options
	 * @returns whether or not the file passes validation
	 */
	private validateOptions(file: File, options: IUploadOptions, rejectHandler?: (errors: IUploadError[]) => void): boolean {

		const errors: IUploadError[] = [];

		if (options) {
			if (!!options.allowTypes) {
				let isValid = false;

				for (const type of options.allowTypes) {
					if (file.type.match(type)) {
						isValid = true;
						break;
					}
				}

				if (!isValid) {
					errors.push(this.getError('FILE_TYPE', { type: file.type }));
				}
			}

			iDef(options.maxSize, (maxSize) => {
				if (maxSize > 0 && file.size > maxSize) {
					errors.push(this.getError('SIZE', { size: file.size }));
				}
			});
		}

		if (errors.length > 0) {
			if (rejectHandler) {
				rejectHandler(errors);
			}
			return false;
		}
		return true;
	}

	protected getError(type: keyof typeof UploadErrors, args?: object): IUploadError {
		return IUploadService.getError(type, args);
	}
}




// WEBPACK FOOTER //
// ./src/core/service/upload/IUploadService.ts